import { Button, Progress } from './ui';
import { useFormats, useT } from '../i18n';
import { useStorage } from '../hooks';
import { cn } from '../lib/cn';

/**
 * Consumo de almacenamiento.
 *
 * Siempre desde la API: la cuota depende del plan, y el plan vive en la base
 * de datos. Cualquier cifra escrita en el frontend se desincronizaría el día
 * que cambien los planes, que es precisamente lo que se puede hacer sin
 * desplegar.
 */
/**
 * Tamaño sin contexto de idioma.
 *
 * Se conserva para los pocos sitios que no son componentes; dentro de React se
 * usa `useFormats().bytes`, que aplica el separador decimal del idioma —«1,5 GB»
 * en español, «1.5 GB» en inglés—.
 */
export function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const unidades = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const escala = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), unidades.length - 1);
  const valor = bytes / 1024 ** escala;
  return `${valor >= 100 || escala === 0 ? Math.round(valor) : valor.toFixed(1)} ${unidades[escala]}`;
}

export function StorageMeter({
  workspaceId,
  compact,
  showPercentage = true,
  showRemaining = true,
  showUpgrade = true,
  className,
}: {
  workspaceId: string | null;
  compact?: boolean;
  showPercentage?: boolean;
  showRemaining?: boolean;
  showUpgrade?: boolean;
  className?: string;
}) {
  const { quota, loading } = useStorage(workspaceId);
  const t = useT();
  const { bytes } = useFormats();

  if (loading || !quota) {
    return <div className={cn('h-16 animate-pulse rounded-xl bg-surface-2/60', className)} />;
  }

  const porcentaje = Math.round(quota.usedRatio * 100);
  const lleno = quota.availableBytes <= 0;
  const alerta = porcentaje >= 90;
  const aviso = porcentaje >= 80;

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <span className={cn('font-medium', compact ? 'text-xs' : 'text-sm')}>
          {t('storage.used')}
        </span>
        {showPercentage ? (
          <span
            className={cn(
              'text-xs tabular-nums',
              alerta ? 'text-danger' : aviso ? 'text-warning' : 'text-muted',
            )}
          >
            {porcentaje}%
          </span>
        ) : null}
      </div>

      <Progress value={quota.usedBytes} max={quota.limitBytes} label={t('storage.used')} />

      <p className={cn('tabular-nums text-muted', compact ? 'text-[11px]' : 'text-xs')}>
        {bytes(quota.usedBytes)} / {bytes(quota.limitBytes)}
      </p>

      {lleno ? (
        <p className="text-xs font-medium text-danger">{t('storage.full')}</p>
      ) : showRemaining ? (
        <p className={cn('text-muted', compact ? 'text-[11px]' : 'text-xs')}>
          {t('storage.remaining', { amount: bytes(quota.availableBytes) })}
        </p>
      ) : null}

      {showUpgrade && aviso ? (
        <Button
          to="/pricing"
          size="sm"
          variant={alerta ? 'primary' : 'secondary'}
          className="w-full"
        >
          {t('storage.upgrade')}
        </Button>
      ) : null}
    </div>
  );
}
